import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, MapPin, Music, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Podcast } from '@/types/Podcast';

interface AirbnbSearchBarProps {
  podcasts: Podcast[];
  onSearchResults: (results: Podcast[]) => void;
  onPodcastSelect?: (podcast: Podcast) => void;
  onCreateVisit?: (searchText: string) => void;
  placeholder?: string;
  language?: 'FR' | 'EN';
  isCompact?: boolean;
  fullWidthOnMobile?: boolean;
  sideMarginPx?: number;
  reportResultsWhileTyping?: boolean;
}

const AirbnbSearchBar: React.FC<AirbnbSearchBarProps> = ({
  podcasts,
  onSearchResults,
  onPodcastSelect,
  onCreateVisit,
  placeholder = "diskover a place",
  language = 'FR',
  isCompact = false,
  fullWidthOnMobile = false,
  sideMarginPx = 36,
  reportResultsWhileTyping = false
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Podcast[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; right: number } | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Normalize text for better search (remove accents, special chars)
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics/accents
      .replace(/[^\w\s]/g, '') // Remove special characters except word chars and spaces
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  };

  // Input sanitization function
  const sanitizeInput = (input: string): string => {
    return input.replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/['"]/g, '') // Remove quotes
    .trim().substring(0, 100); // Limit length but keep more chars for better search
  };

  // Improved search function with fuzzy matching
  const searchInText = (searchTerm: string, text: string): boolean => {
    const normalizedSearch = normalizeText(searchTerm);
    const normalizedText = normalizeText(text);
    
    // Direct match
    if (normalizedText.includes(normalizedSearch)) {
      return true;
    }
    
    // Split search term and check if all parts are found
    const searchWords = normalizedSearch.split(' ').filter(w => w.length > 1);
    if (searchWords.length > 1) {
      return searchWords.every(word => normalizedText.includes(word));
    }
    
    // For single words, check partial matching
    if (normalizedSearch.length >= 3) {
      return normalizedText.split(' ').some(word => 
        word.startsWith(normalizedSearch) || 
        (normalizedSearch.length >= 4 && word.includes(normalizedSearch))
      );
    }
    
    return false;
  };

  // Handle window resize for mobile detection and dropdown positioning
  useEffect(() => {
    const updatePosition = () => {
      setIsMobile(window.innerWidth < 768);
      if (inputRef.current && fullWidthOnMobile && window.innerWidth < 768) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: sideMarginPx,
          right: sideMarginPx
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('orientationchange', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('orientationchange', updatePosition);
    };
  }, [fullWidthOnMobile, sideMarginPx]);

  // Generate suggestions based on query
  useEffect(() => {
    const sanitizedQuery = sanitizeInput(query);
    if (sanitizedQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      if (reportResultsWhileTyping) {
        onSearchResults(podcasts);
      }
      return;
    }
    const filtered = podcasts.filter(podcast => {
      const description = language === 'FR' ? podcast.Description_FR : podcast.Description_EN || podcast.Description_FR;
      
      return searchInText(sanitizedQuery, podcast.Name) || 
             searchInText(sanitizedQuery, podcast.City) || 
             searchInText(sanitizedQuery, podcast.Country) || 
             (description && searchInText(sanitizedQuery, description));
    });

    // Sort results to prioritize exact city matches
    const sorted = filtered.sort((a, b) => {
      const normalizedQuery = normalizeText(sanitizedQuery);
      const aCityMatch = normalizeText(a.City) === normalizedQuery;
      const bCityMatch = normalizeText(b.City) === normalizedQuery;
      const aNameMatch = normalizeText(a.Name) === normalizedQuery;
      const bNameMatch = normalizeText(b.Name) === normalizedQuery;

      // Prioritize exact city matches first
      if (aCityMatch && !bCityMatch) return -1;
      if (!aCityMatch && bCityMatch) return 1;

      // Then prioritize exact name matches
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;

      return 0;
    });

    setSuggestions(sorted.slice(0, 5)); // Limit to 5 suggestions
    setShowSuggestions(true);
    
    // Report results while typing if enabled
    if (reportResultsWhileTyping) {
      onSearchResults(sorted);
    }
  }, [query, podcasts, language, reportResultsWhileTyping, onSearchResults]);

  // Handle search
  const handleSearch = () => {
    const sanitizedQuery = sanitizeInput(query);
    if (sanitizedQuery.length < 2) {
      onSearchResults(podcasts);
      return;
    }
    const filtered = podcasts.filter(podcast => {
      const description = language === 'FR' ? podcast.Description_FR : podcast.Description_EN || podcast.Description_FR;
      
      return searchInText(sanitizedQuery, podcast.Name) || 
             searchInText(sanitizedQuery, podcast.City) || 
             searchInText(sanitizedQuery, podcast.Country) || 
             (description && searchInText(sanitizedQuery, description));
    });

    // Sort results to prioritize exact city matches
    const sorted = filtered.sort((a, b) => {
      const normalizedQuery = normalizeText(sanitizedQuery);
      const aCityMatch = normalizeText(a.City) === normalizedQuery;
      const bCityMatch = normalizeText(b.City) === normalizedQuery;
      const aNameMatch = normalizeText(a.Name) === normalizedQuery;
      const bNameMatch = normalizeText(b.Name) === normalizedQuery;

      // Prioritize exact city matches first
      if (aCityMatch && !bCityMatch) return -1;
      if (!aCityMatch && bCityMatch) return 1;

      // Then prioritize exact name matches
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;

      return 0;
    });

    onSearchResults(sorted);
    setShowSuggestions(false);
  };

  // Handle suggestion click
  const handleSuggestionClick = (podcast: Podcast) => {
    setQuery(podcast.Name);
    setShowSuggestions(false);
    
    // Use onPodcastSelect if available, otherwise fallback to direct navigation
    if (onPodcastSelect) {
      onPodcastSelect(podcast);
    } else {
      navigate(`/place/${encodeURIComponent(podcast.Name)}`);
    }
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    onSearchResults(podcasts);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle focus for updating dropdown position
  const handleFocus = () => {
    if (query.length >= 2) {
      setShowSuggestions(true);
      if (inputRef.current && fullWidthOnMobile && isMobile) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: sideMarginPx,
          right: sideMarginPx
        });
      }
    }
  };

  // Render dropdown content
  const renderDropdown = () => {
    if (!showSuggestions) return null;

    const dropdownContent = (
      <>
        {suggestions.length > 0 && (
          <Card className={`${
            fullWidthOnMobile && isMobile
              ? 'fixed bg-white dark:bg-gray-900 border shadow-xl rounded-2xl overflow-y-auto max-h-[60vh]'
              : 'absolute top-full left-6 right-6 z-[100] max-h-80 overflow-y-auto bg-white dark:bg-gray-900 border shadow-xl rounded-2xl mt-2'
          }`}
          style={fullWidthOnMobile && isMobile && dropdownPosition ? {
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 10000
          } : undefined}
          >
            <div className="p-1">
              {suggestions.map((podcast, index) => (
                <div
                  key={`${podcast.Name}-${index}`}
                  onClick={() => handleSuggestionClick(podcast)}
                  className="flex items-start gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-all duration-200 border-b border-gray-100/50 dark:border-gray-800/50 last:border-b-0"
                >
                  {podcast.Image ? (
                    <img 
                      src={podcast.Image} 
                      alt={podcast.Name} 
                      className="w-12 h-12 object-cover rounded-lg flex-shrink-0" 
                      loading="lazy" 
                      decoding="async" 
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{podcast.Name}</h4>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{podcast.City}, {podcast.Country}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-1 mt-1">
                      {language === 'FR' ? podcast.Description_FR : podcast.Description_EN || podcast.Description_FR}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    <Music className="w-4 h-4 text-blue-500" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {suggestions.length === 0 && query.length >= 2 && (
          <Card className={`${
            fullWidthOnMobile && isMobile
              ? 'fixed bg-white dark:bg-gray-900 border shadow-xl rounded-2xl'
              : 'absolute top-full left-6 right-6 z-[100] bg-white dark:bg-gray-900 border shadow-xl rounded-2xl mt-2'
          }`}
          style={fullWidthOnMobile && isMobile && dropdownPosition ? {
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 10000
          } : undefined}
          >
            <div className="p-5 flex flex-col items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center">
                {language === 'EN' ? `No place found for "${query}"` : `Aucun lieu trouvé pour "${query}"`}
              </p>
              {onCreateVisit && (
                <button
                  onClick={() => {
                    onCreateVisit(query);
                    setShowSuggestions(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span>
                    {language === 'EN' ? `Create a visit for "${query}"` : `Créer une visite pour "${query}"`}
                  </span>
                </button>
              )}
            </div>
          </Card>
        )}
      </>
    );

    // Use portal for mobile full-width dropdown
    if (fullWidthOnMobile && isMobile && dropdownPosition) {
      return createPortal(dropdownContent, document.body);
    }

    return dropdownContent;
  };

  return (
    <div className={`relative w-full transition-all duration-300 ${
      isCompact 
        ? '' 
        : 'px-6 py-4 bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-800/50'
    }`}>
      <div 
        ref={inputRef}
        className={`relative mx-auto transition-all duration-300 ${
          isCompact ? 'max-w-sm' : 'max-w-md'
        }`}
      >
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
          <Search className="w-5 h-5 text-gray-500" />
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          className={`w-full pl-12 pr-12 text-base border border-gray-300 dark:border-gray-700 rounded-full bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600 focus:border-gray-400 dark:focus:border-gray-600 focus:ring-2 focus:ring-gray-100 dark:focus:ring-gray-800 transition-all duration-300 ${
            isCompact 
              ? 'h-10 shadow-sm hover:shadow' 
              : 'h-12 shadow-sm hover:shadow-md focus:shadow-md'
          }`}
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Render dropdown */}
      {renderDropdown()}
    </div>
  );
};

export default AirbnbSearchBar;